import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import RIcon from "../reservas/components/ReservasIcons";
import { BEAUTY_FALLBACK_IMAGE, resolveUrl } from "../reservas/components/ReservaShared";
import SalonContent from "./components/SalonContent";
import BookingAside from "./components/BookingAside";
import { isOnlineBookingDisabled } from "../reservas/onlineBooking";
import { SucursalBanner } from "../reservas/components/Sucursales";
import { backTarget } from "../reservas/components/sucursalesModel";
import { OpcionesModal } from "./components/OpcionesDeServicio";
import { tieneOpciones } from "../../helpers/beautyLineas";
import { formatMoney, minServicePrice, salonAddressLine, salonPolicyLines, todayOpenStatus } from "./beautyBooking";
import "../reservas/reservas.css";
import "./beauty.css";

/**
 * Desktop salon info view. Same structure as the Canchas club info view:
 * crumb, rounded hero, info tabs + stacked sections and the sticky cart aside.
 */
const SalonDetailDesktop = ({
  salon,
  services,
  professionals,
  cart = [],
  choices = {},
  onAdd,
  onUpdate,
  onRemove,
  onStartBooking,
  topSlot = null,
  sucursales = [],
  casaCentral = null,
  heroSalon = null,
  sedes = null,
  sedeStatus = null,
}) => {
  // On a casa central the hero stays the casa's while `salon` is the chosen sede.
  const hero = heroSalon || salon;
  const navigate = useNavigate();
  const img = resolveUrl(hero.image || hero.mobileimage) || BEAUTY_FALLBACK_IMAGE;
  const minPrice = minServicePrice(services);
  const address = salonAddressLine(hero);
  const eyebrow = (hero.serviceCategories || []).filter(Boolean).join(" · ");
  const openStatus = todayOpenStatus(hero.openingHours);
  const hasServices = services.length > 0;
  const cartIds = cart.map((s) => String(s._id));
  const back = backTarget("beauty", casaCentral);
  const goBack = () => navigate(back.path);
  // Service whose options modal is open (add or edit).
  const [picking, setPicking] = useState(null);
  const startAdd = (service) => (tieneOpciones(service) ? setPicking(service) : onAdd?.(service));
  const confirmPick = (choice) => {
    if (cartIds.includes(String(picking._id))) onUpdate?.(picking, choice);
    else onAdd?.(picking, choice);
    setPicking(null);
  };

  return (
    <div className="reservas-root beauty-desktop">
      <div className="wrap bar-detail">
        <button type="button" className="crumb" onClick={goBack}>
          <RIcon name="chevL" width={16} height={16} /> {back.label}
        </button>

        <SucursalBanner kind="beauty" casaCentral={casaCentral} />

        <div className="bd-hero">
          <img src={img} alt={hero.name} onError={(e) => { e.currentTarget.src = BEAUTY_FALLBACK_IMAGE; }} />
          <div className="bd-hero-scrim" />
          <div className="bd-hero-info">
            <h1>{hero.name}</h1>
            <div className="meta">
              {address && <span><RIcon name="pin" width={16} height={16} /> {address}</span>}
              {eyebrow && <span>{eyebrow}</span>}
              {openStatus && (
                <span data-testid="beauty-open-status"><RIcon name="clock" width={16} height={16} /> {openStatus.text}</span>
              )}
              {hasServices && minPrice !== null && <span>Desde {formatMoney(minPrice)}</span>}
            </div>
          </div>
          {hero.reservationMode === "deposit" && (
            <span className="bd-pill">
              <RIcon name="shield" width={15} height={15} /> Con seña
            </span>
          )}
        </div>

        {topSlot}

        <div className="bd-grid bd-info-grid">
          <div>
            <SalonContent
              salon={salon}
              services={services}
              professionals={professionals}
              isDesktop
              cartIds={cartIds}
              onAdd={hasServices && onAdd && !sedeStatus ? startAdd : undefined}
              onEdit={setPicking}
              onRemove={onRemove}
              onBack={goBack}
              sucursales={sucursales}
              sedes={sedes}
              sedeStatus={sedeStatus}
            />
          </div>
          {(hasServices || sedeStatus) && (
            <BookingAside
              salonName={salon.name}
              cart={cart}
              choices={choices}
              policyLines={salonPolicyLines(salon)}
              onRemove={onRemove}
              onEdit={setPicking}
              onStartBooking={onStartBooking}
              bookingDisabled={isOnlineBookingDisabled(salon)}
              sucursales={sucursales}
              casaCentral={casaCentral}
              sedes={sedes}
            />
          )}
        </div>
      </div>
      {picking && (
        <OpcionesModal
          service={picking}
          initial={cartIds.includes(String(picking._id)) ? choices[String(picking._id)] : undefined}
          onConfirm={confirmPick}
          onClose={() => setPicking(null)}
        />
      )}
    </div>
  );
};

export default SalonDetailDesktop;
